import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Avatar } from "../components/Avatar.js";
import { ContactEditor } from "../components/ContactFields.js";
import {
  Achievements,
  Form,
  PeopleTable,
  ProfileName,
  RatingCards,
  RatingTrend,
  SectionHead,
  Stats,
} from "../components/profile.js";
import { Button, inputStyle, Notice, Segmented } from "../components/ui.js";
import { hasRole, useAuth } from "../lib/auth.js";
import { fetchMatches } from "../lib/matches.js";
import { uploadAvatar } from "../lib/avatars.js";
import {
  addManagedMember,
  fetchMyChildren,
  renameManagedMember,
  setMemberCategory,
  setMemberPlayingAsSelf,
} from "../lib/members.js";
import { fetchRatings } from "../lib/ratings.js";
import {
  PROFILE_HISTORY,
  formFor,
  opponentsFor,
  partnersFor,
  recordFor,
  winRate,
} from "../lib/stats.js";
import type { MemberCategory, PlayingAs } from "../lib/types.js";
import { playingAs } from "../lib/types.js";

type Tab = "game" | "details";

/**
 * Your own profile, in two halves that answer two different questions.
 *
 * **Your game** is everything `/player/:id` shows about you — the same shared components, so
 * the two never disagree about your record. **Your details** is everything you can change:
 * how you play, your category, your picture, contact details, your children, your account.
 *
 * They were one long page, with the settings that decide which events you may enter tucked
 * into the header as tiny selects and the children form at the very bottom. The register
 * screen sends people here to fix exactly those things, so each section of Details has an
 * id and the tab lives in the URL: `/me?tab=details#children` lands on the right form.
 */
export function Me() {
  const { member, avatarUrl } = useAuth();
  const [params] = useSearchParams();
  const location = useLocation();
  const tab: Tab = params.get("tab") === "details" ? "details" : "game";

  // React Router does not scroll to a fragment on its own. Wait for the tab to render, then
  // bring the named section into view — the whole point of the deep links.
  useEffect(() => {
    if (!location.hash) return;
    document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: "start" });
  }, [tab, location.hash]);

  return (
    <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
      <header className="mb-5 flex items-center gap-4">
        <Avatar
          name={member?.displayName ?? "You"}
          url={member?.avatarUrl ?? avatarUrl}
          size={64}
        />
        <div className="min-w-0">
          <ProfileName name={member?.displayName ?? "You"} />
          {member && member.role !== "player" ? (
            <div className="font-mono mt-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-muted">
              {member.role}
            </div>
          ) : null}
        </div>
      </header>

      <nav
        aria-label="Your profile"
        className="mb-2 grid grid-cols-2 border-2"
        style={{ borderColor: "var(--color-line)" }}
      >
        {(
          [
            { value: "game", label: "Your game", to: "/me" },
            { value: "details", label: "Your details", to: "/me?tab=details" },
          ] as const
        ).map((item) => (
          <Link
            key={item.value}
            to={item.to}
            replace
            aria-current={tab === item.value ? "page" : undefined}
            className="flex min-h-[44px] items-center justify-center text-sm font-semibold"
            style={{
              background: tab === item.value ? "var(--color-line)" : "transparent",
              color: tab === item.value ? "var(--color-ground)" : "var(--color-ink)",
            }}
          >
            {item.label}
          </Link>
        ))}
      </nav>

      {member ? tab === "game" ? <YourGame memberId={member.id} /> : <YourDetails /> : null}
    </main>
  );
}

/** Everything another player sees about you, from the same components their screen uses. */
function YourGame({ memberId }: { memberId: string }) {
  const { data: ratings = [], isPending } = useQuery({
    queryKey: ["ratings", memberId],
    queryFn: () => fetchRatings(memberId),
  });

  // The same history the public profile is built from, so the two agree on your record.
  const { data: page } = useQuery({
    queryKey: ["player-matches", memberId],
    queryFn: () => fetchMatches({ memberId }, { limit: PROFILE_HISTORY }),
  });

  const matches = page?.matches ?? [];
  const record = recordFor(matches, memberId);
  const played = record.won + record.lost;
  const rate = winRate(record);

  return (
    <>
      {isPending ? <Notice>Loading your ratings…</Notice> : null}

      {!isPending && ratings.length === 0 ? (
        <Notice>
          No rating yet. It starts at your self-assessed level and moves fast for the first ten
          matches.
        </Notice>
      ) : null}

      <RatingCards ratings={ratings} />

      <RatingTrend memberId={memberId} ratings={ratings} />

      <Form results={formFor(matches, memberId)} />

      <Stats
        items={[
          { value: String(played), label: "Played" },
          { value: String(record.won), label: "Won" },
          { value: String(record.lost), label: "Lost" },
          { value: rate === null ? "—" : `${rate}%`, label: "Win rate" },
        ]}
      />

      <Achievements memberId={memberId} />

      <PeopleTable
        title="You play with"
        people={partnersFor(matches, memberId)}
        empty="No doubles partners yet — log a doubles match and they show up here."
      />

      <PeopleTable
        title="You play against"
        people={opponentsFor(matches, memberId)}
        empty="Nobody yet."
      />

      <p className="mt-4 text-sm font-semibold">
        <Link className="underline underline-offset-4" to={`/matches?player=${memberId}`}>
          All your matches
        </Link>
      </p>
    </>
  );
}

/**
 * Everything you can change, in the order people come looking for it: who you are as a
 * player (which decides the events you may enter), how organisers reach you, the children
 * you act for, and your account.
 */
function YourDetails() {
  const { member } = useAuth();
  if (!member) return null;

  return (
    <>
      <AboutYou />
      <YourContact memberId={member.id} />
      <YourChildren guardianId={member.id} />
      <Account />
    </>
  );
}

/**
 * Picture, how you play, category — each a labelled row with its control in plain view.
 * These used to be selects styled as caption text in the header, which is to say invisible.
 */
function AboutYou() {
  const { member, avatarUrl, reloadMember } = useAuth();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);

  const refresh = async () => {
    await reloadMember();
    void queryClient.invalidateQueries({ queryKey: ["members"] });
    void queryClient.invalidateQueries({ queryKey: ["leaderboard"] });
  };

  // Yours to fill in while it is empty; after that an admin's, because it decides which
  // events you may enter (20260930000100). RLS has always allowed editing your own row.
  const recategorise = useMutation({
    mutationFn: (category: MemberCategory) => setMemberCategory(member!.id, category),
    onSuccess: refresh,
  });

  // How you play, your own fact to change (20261001000700). Was an admin's until now, which
  // made "I play as a senior" a request rather than an edit.
  const replay = useMutation({
    mutationFn: (as: PlayingAs) => setMemberPlayingAsSelf(member!.id, as),
    onSuccess: refresh,
  });

  const upload = useMutation({
    mutationFn: (file: File) => uploadAvatar(member!.id, file),
    onSuccess: refresh,
  });

  if (!member) return null;

  // The member row wins over the session. Google's picture is only a starting value; once
  // someone uploads their own, the session still carries the Google URL forever and would
  // otherwise keep overriding the thing they just changed.
  const picture = member.avatarUrl ?? avatarUrl;
  const failure = recategorise.error ?? replay.error ?? upload.error;

  return (
    <section id="about" className="mt-8 scroll-mt-4">
      <SectionHead title="About you" />

      <Row label="Picture">
        <div className="flex items-center gap-3">
          <Avatar name={member.displayName} url={picture} size={40} />
          <Button
            onClick={() => input.current?.click()}
            disabled={upload.isPending}
            className="shrink-0"
          >
            {upload.isPending ? "Uploading…" : "Change picture"}
          </Button>
          <input
            ref={input}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              // Cleared so choosing the same file twice still fires a change event — which
              // is exactly what someone does after a failed upload.
              event.target.value = "";
              if (file) upload.mutate(file);
            }}
          />
        </div>
      </Row>

      <Row
        label="How you play"
        hint="Decides which events you can enter — juniors' events, seniors' events, or the rest."
      >
        <Segmented
          label="How you play"
          value={playingAs(member)}
          onChange={(as) => replay.mutate(as)}
          options={[
            { value: "junior", label: "Junior" },
            { value: "adult", label: "Adult" },
            { value: "senior", label: "Senior" },
          ]}
        />
      </Row>

      <Row
        label="Category"
        hint={
          member.category
            ? "Only an admin can change this once it’s set."
            : "Needed before you can enter a men’s or women’s event. Once set, only an admin can change it."
        }
      >
        {member.category ? (
          <span className="text-base font-semibold">
            {member.category === "men"
              ? member.junior
                ? "Boy"
                : "Men"
              : member.junior
                ? "Girl"
                : "Women"}
          </span>
        ) : (
          <Segmented
            label="Your category"
            value={"" as MemberCategory}
            onChange={(category) => recategorise.mutate(category)}
            options={[
              { value: "men", label: member.junior ? "Boy" : "Men" },
              { value: "women", label: member.junior ? "Girl" : "Women" },
            ]}
          />
        )}
      </Row>

      {failure ? <Notice tone="attention">{failure.message}</Notice> : null}
    </section>
  );
}

/** A labelled setting: the label above, the control, and a line on what it changes. */
function Row({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="border-b py-4" style={{ borderColor: "var(--color-line-muted)" }}>
      <div className="mb-2 text-[15px] font-semibold">{label}</div>
      {children}
      {hint ? <p className="mt-2 text-sm text-ink-muted">{hint}</p> : null}
    </div>
  );
}

/** Your account: the admin's way into the members list, signing out, and the fine print. */
function Account() {
  const { member, signOut } = useAuth();

  return (
    <section id="account" className="mt-8 scroll-mt-4">
      <SectionHead title="Account" />
      <ul>
        {hasRole(member, "admin") ? <AccountLink to="/admin" label="Members" /> : null}
        <AccountLink to="/rating" label="How ratings work" />
        <AccountLink to="/privacy" label="Privacy policy" />
      </ul>
      <Button className="mt-5 w-full" onClick={() => void signOut()}>
        Sign out
      </Button>
    </section>
  );
}

function AccountLink({ to, label }: { to: string; label: string }) {
  return (
    <li className="border-b" style={{ borderColor: "var(--color-line-muted)" }}>
      <Link to={to} className="flex min-h-[52px] items-center justify-between gap-3">
        <span className="text-[15px] font-semibold">{label}</span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          aria-hidden
        >
          <path d="M9 5l7 7-7 7" />
        </svg>
      </Link>
    </li>
  );
}

/**
 * Your flat number and phone number. Here, and nowhere a player can see: `/player/:id` never
 * shows them, because only you, organisers and admins may read them (20261001000300).
 */
function YourContact({ memberId }: { memberId: string }) {
  return (
    <section id="contact" className="mt-8 scroll-mt-4">
      <SectionHead title="Contact details" aside="Optional" />
      <p className="my-3 text-sm text-ink-muted">
        Only you, organisers and admins see these. Some tournaments ask for both before you can
        enter, or before you can enter a child.
      </p>
      <ContactEditor memberId={memberId} />
    </section>
  );
}

/**
 * Children without a login of their own, who you enter in events.
 *
 * Here rather than on a screen of its own because it belongs to you the way your picture
 * does, and because the moment a parent needs it is the moment they are already looking at
 * their own profile after being told "add your kid in the app". A name is all it asks for,
 * and all that is kept — see 20260929000100.
 */
function YourChildren({ guardianId }: { guardianId: string }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [childCategory, setChildCategory] = useState<MemberCategory | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const [adding, setAdding] = useState(false);

  const { data: children = [] } = useQuery({
    queryKey: ["children", guardianId],
    queryFn: () => fetchMyChildren(guardianId),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["children"] });
    void queryClient.invalidateQueries({ queryKey: ["members"] });
  };

  const add = useMutation({
    // Yourself as guardian, said out loud. A player becomes guardian either way, but an
    // organiser adding a child with no guardian named creates one nobody looks after — right
    // for the entry list, wrong for your own child.
    mutationFn: () => addManagedMember(name, childCategory!, guardianId),
    onSuccess: () => {
      setName("");
      setChildCategory(null);
      setAdding(false);
      refresh();
    },
  });

  const rename = useMutation({
    mutationFn: (next: { id: string; name: string }) => renameManagedMember(next.id, next.name),
    onSuccess: () => {
      setRenaming(null);
      refresh();
    },
  });

  const busy = add.isPending || rename.isPending;
  const failure = add.error ?? rename.error;

  return (
    <section id="children" className="mt-8 scroll-mt-4">
      <SectionHead title="Your children" aside="You enter them" />
      <p className="my-3 text-sm text-ink-muted">
        For a child without a login of their own. You can enter them in events for them. Only their
        name, and whether they&rsquo;re a boy or a girl, is kept.
      </p>

      {children.map((child) =>
        renaming?.id === child.id ? (
          <form
            key={child.id}
            className="flex items-center gap-2 border-b py-3"
            style={{ borderColor: "var(--color-line-muted)" }}
            onSubmit={(event) => {
              event.preventDefault();
              rename.mutate(renaming);
            }}
          >
            <input
              value={renaming.name}
              onChange={(event) => setRenaming({ id: child.id, name: event.target.value })}
              maxLength={60}
              aria-label={`New name for ${child.displayName}`}
              style={inputStyle}
              autoFocus
            />
            <Button
              tone="primary"
              type="submit"
              className="shrink-0"
              disabled={busy || !renaming.name.trim()}
            >
              {rename.isPending ? "Saving…" : "Save"}
            </Button>
            <Button
              tone="quiet"
              type="button"
              className="shrink-0"
              onClick={() => setRenaming(null)}
              disabled={busy}
            >
              Cancel
            </Button>
          </form>
        ) : (
          <div
            key={child.id}
            className="flex min-h-[60px] items-center gap-2.5 border-b"
            style={{ borderColor: "var(--color-line-muted)" }}
          >
            <Avatar name={child.displayName} url={null} size={30} />
            <Link to={`/player/${child.id}`} className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="truncate text-[15px] font-semibold">{child.displayName}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-ink-muted">
                {child.junior ? "Junior · " : ""}
                {child.category === "men"
                  ? "Boy"
                  : child.category === "women"
                    ? "Girl"
                    : "No category"}
                {" · no login"}
              </span>
            </Link>
            <Button
              tone="quiet"
              onClick={() => setRenaming({ id: child.id, name: child.displayName })}
              disabled={busy}
            >
              Rename
            </Button>
          </div>
        ),
      )}

      {adding ? (
        <form
          className="mt-4 flex flex-col gap-3 border-2 p-3"
          style={{ borderColor: "var(--color-line)" }}
          onSubmit={(event) => {
            event.preventDefault();
            add.mutate();
          }}
        >
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold">Name</span>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={60}
              autoFocus
              style={inputStyle}
            />
          </label>
          {/* Required, and not pre-selected: which events a child can enter depends on it,
              and a pre-selected answer is one people submit without reading. */}
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold">Boy or girl</span>
            <Segmented
              label="Boy or girl"
              value={(childCategory ?? "") as MemberCategory}
              onChange={(value) => setChildCategory(value)}
              options={[
                { value: "men", label: "Boy" },
                { value: "women", label: "Girl" },
              ]}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button
              tone="primary"
              type="submit"
              disabled={busy || !name.trim() || childCategory === null}
            >
              {add.isPending ? "Adding…" : "Add child"}
            </Button>
            <Button
              type="button"
              onClick={() => {
                setAdding(false);
                setName("");
                setChildCategory(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <Button className="mt-4 w-full" onClick={() => setAdding(true)} disabled={busy}>
          + Add a child
        </Button>
      )}

      {failure ? <Notice tone="attention">{failure.message}</Notice> : null}
    </section>
  );
}
